import { Injectable, computed, inject, signal } from '@angular/core';

import { AuthService } from '@auth/services/auth.service';
import { PermissionsService } from '@auth/services/permissions.service';
import { MenuItem, buildNavigationModel } from '@layout/components/app.menu';

import { ENTRY_DESCRIPTIONS } from './command-palette.descriptions';

/** Entrada navegable aplanada del catálogo de navegación. */
export interface SearchEntry {
  /** Identificador estable derivado del routerLink. */
  id: string;
  label: string;
  /** Sección/módulo padre, usado como badge y para agrupar. */
  group: string;
  /** Descripción corta de la página, mostrada como subtítulo. */
  description?: string;
  icon: string;
  routerLink: string[];
  permissionId?: string;
  /** Basta con tener uno (espejo de `MenuItem.permissionIdsAny`). */
  permissionIdsAny?: string[];
  /** Hay que tenerlos todos (espejo de `MenuItem.permissionIdsAll`). */
  permissionIdsAll?: string[];
  permissionModuleId?: string;
}

const RECENTS_KEY = 'command-palette.recents';
const RECENTS_LIMIT = 5;

/** Rango de marcas diacríticas combinantes (U+0300–U+036F). */
const DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

/** Normaliza a minúsculas y elimina acentos (locale es). */
const normalize = (value: string): string => value.toLowerCase().normalize('NFD').replace(DIACRITICS, '');

@Injectable({ providedIn: 'root' })
export class CommandPaletteService {
  private readonly auth = inject(AuthService);
  private readonly permissions = inject(PermissionsService);

  // ── Estado del overlay ──────────────────────────────────────────────
  readonly isOpen = signal(false);
  readonly query = signal('');

  open(): void {
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
    this.query.set('');
  }

  toggle(): void {
    if (this.isOpen()) this.close();
    else this.open();
  }

  // ── Catálogo aplanado ───────────────────────────────────────────────
  private readonly entries = computed<SearchEntry[]>(() => {
    const model = buildNavigationModel(this.auth.isAdmin());
    const out: SearchEntry[] = [];

    const walk = (items: MenuItem[] | undefined, group: string, inheritedModuleId?: string): void => {
      if (!items) return;
      for (const item of items) {
        if (item.separator || item.visible === false) continue;
        const moduleId = item.permissionModuleId ?? inheritedModuleId;
        if (item.items?.length) {
          walk(item.items, item.label ?? group, moduleId);
        } else if (item.routerLink?.length) {
          const id = item.routerLink.join('/');
          out.push({
            id,
            label: item.label ?? '',
            group,
            description: ENTRY_DESCRIPTIONS[id],
            icon: item.icon?.replace('pi-fw ', '').replace('pi-fw', '') ?? 'pi pi-circle',
            routerLink: item.routerLink,
            permissionId: item.permissionId,
            permissionIdsAny: item.permissionIdsAny,
            permissionIdsAll: item.permissionIdsAll,
            permissionModuleId: moduleId,
          });
        }
      }
    };

    for (const top of model) {
      if (top.separator || top.visible === false) continue;
      walk(top.items, top.label ?? '', top.permissionModuleId);
    }

    return out;
  });

  /** Entradas a las que el usuario actual tiene acceso. */
  readonly availableEntries = computed<SearchEntry[]>(() =>
    this.entries().filter((entry) => {
      if (entry.permissionModuleId && !this.permissions.hasModuleAccess(entry.permissionModuleId)) return false;
      if (entry.permissionId && !this.permissions.hasPermission(entry.permissionId)) return false;
      // Mismas reglas que el menú lateral: el paleta es otro acceso a la misma ruta, así que un
      // ítem gateado por varios permisos no puede colarse por aquí (acabaría en un 403).
      if (entry.permissionIdsAny?.length && !entry.permissionIdsAny.some((id) => this.permissions.hasPermission(id))) {
        return false;
      }
      if (entry.permissionIdsAll?.length && !entry.permissionIdsAll.every((id) => this.permissions.hasPermission(id))) {
        return false;
      }
      return true;
    })
  );

  // ── Recientes ───────────────────────────────────────────────────────
  private readonly recentIds = signal<string[]>(this.loadRecents());

  readonly recentEntries = computed<SearchEntry[]>(() => {
    const byId = new Map(this.availableEntries().map((entry) => [entry.id, entry]));
    return this.recentIds()
      .map((id) => byId.get(id))
      .filter((entry): entry is SearchEntry => entry !== undefined);
  });

  addRecent(id: string): void {
    const next = [id, ...this.recentIds().filter((existing) => existing !== id)].slice(0, RECENTS_LIMIT);
    this.recentIds.set(next);
    try {
      localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
    } catch {
      // localStorage no disponible (modo privado); los recientes quedan solo en memoria.
    }
  }

  clearRecents(): void {
    this.recentIds.set([]);
    try {
      localStorage.removeItem(RECENTS_KEY);
    } catch {
      // localStorage no disponible (modo privado); basta con limpiar el estado en memoria.
    }
  }

  private loadRecents(): string[] {
    try {
      const raw = localStorage.getItem(RECENTS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
    } catch {
      return [];
    }
  }

  // ── Búsqueda ────────────────────────────────────────────────────────
  /** Resultados ordenados por relevancia para la consulta actual. */
  readonly results = computed<SearchEntry[]>(() => {
    const q = normalize(this.query().trim());
    if (!q) return [];
    return this.availableEntries()
      .map((entry) => ({ entry, score: this.score(entry, q) }))
      .filter((scored) => scored.score > 0)
      .sort((a, b) => b.score - a.score || a.entry.label.localeCompare(b.entry.label))
      .map((scored) => scored.entry);
  });

  private score(entry: SearchEntry, q: string): number {
    const label = normalize(entry.label);
    const group = normalize(entry.group);
    const description = entry.description ? normalize(entry.description) : '';

    let score = 0;
    if (label === q) score = 100;
    else if (label.startsWith(q)) score = 80;
    else if (new RegExp(`\\b${escapeRegExp(q)}`).test(label)) score = 60;
    else if (label.includes(q)) score = 40;
    else if (isSubsequence(q, label)) score = 20;

    if (score === 0) {
      // Permite encontrar por nombre de módulo (ej: "ventas factura")…
      if (group.includes(q)) score = 15;
      // …o por la descripción de la página (ej: "kardex", "flujo de caja").
      else if (description.includes(q)) score = 10;
      else return 0;
    }

    if (group.includes(q)) score += 5;
    if (description.includes(q)) score += 2;
    return score;
  }
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isSubsequence(needle: string, haystack: string): boolean {
  let i = 0;
  for (let j = 0; j < haystack.length && i < needle.length; j++) {
    if (haystack[j] === needle[i]) i++;
  }
  return i === needle.length;
}
